import { useState } from "react";
import { Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { errMsg, toCents, uid } from "@/lib/api";

const price = (c) => ((c || 0) / 100).toFixed(2);

const ChoiceRow = ({ c, upd, del }) => (
  <div className="flex gap-2">
    <Input value={c.name} placeholder="Choice" onChange={(e) => upd({ ...c, name: e.target.value })} data-testid="menu-choice-name-input" />
    <Input className="w-24" defaultValue={price(c.price_cents)} onBlur={(e) => upd({ ...c, price_cents: toCents(e.target.value) })} data-testid="menu-choice-price-input" />
    <button onClick={del} aria-label="Remove choice" className="text-muted2 hover:text-danger"><Trash2 size={16} /></button>
  </div>
);

const OptionEditor = ({ g, upd, del }) => (
  <div className="rounded-xl bg-brand-pale p-3">
    <div className="flex flex-wrap items-center gap-2">
      <Input className="w-40 bg-white" value={g.name} onChange={(e) => upd({ ...g, name: e.target.value })} data-testid="menu-option-name-input" />
      <label className="flex items-center gap-1 text-xs"><Switch checked={g.required} onCheckedChange={(v) => upd({ ...g, required: v })} /> Required</label>
      <label className="flex items-center gap-1 text-xs"><Switch checked={g.multi} onCheckedChange={(v) => upd({ ...g, multi: v })} /> Multiple</label>
      <button onClick={del} className="ml-auto text-xs text-danger" data-testid="menu-option-delete-btn">Remove option</button>
    </div>
    <div className="mt-2 grid gap-2">
      {g.choices.map((c, i) => <ChoiceRow key={c.id} c={c} upd={(nc) => upd({ ...g, choices: g.choices.map((x, j) => (j === i ? nc : x)) })} del={() => upd({ ...g, choices: g.choices.filter((_, j) => j !== i) })} />)}
      <button className="link-lg text-left text-xs" onClick={() => upd({ ...g, choices: [...g.choices, { id: uid(), name: "", price_cents: 0 }] })} data-testid="menu-add-choice-btn">+ Add choice</button>
    </div>
  </div>
);

const ItemEditor = ({ it, upd, del }) => (
  <div className={`rounded-2xl border p-4 ${it.sold_out ? "border-line bg-gray-50" : "border-line"}`} data-testid="menu-item-editor">
    <div className="grid gap-2 sm:grid-cols-[1fr_120px]">
      <Input value={it.name} placeholder="Item name" onChange={(e) => upd({ ...it, name: e.target.value })} data-testid="menu-item-name-input" />
      <Input defaultValue={price(it.price_cents)} onBlur={(e) => upd({ ...it, price_cents: toCents(e.target.value) })} placeholder="Price" data-testid="menu-item-price-input" />
    </div>
    <Input className="mt-2" value={it.description} placeholder="Description" onChange={(e) => upd({ ...it, description: e.target.value })} />
    <div className="mt-3 flex flex-wrap items-center gap-4 text-sm">
      <label className="flex items-center gap-2"><Switch checked={it.sold_out} onCheckedChange={(v) => upd({ ...it, sold_out: v })} data-testid="menu-item-soldout-switch" /> Sold out</label>
      <label className="flex items-center gap-2"><Switch checked={it.tax_exempt} onCheckedChange={(v) => upd({ ...it, tax_exempt: v })} data-testid="menu-item-taxexempt-switch" /> Tax exempt</label>
      <button className="link-lg text-xs" onClick={() => upd({ ...it, options: [...it.options, { id: uid(), name: "Option", required: false, multi: false, choices: [] }] })} data-testid="menu-add-option-btn">+ Add option group</button>
      <button className="ml-auto text-xs text-danger" onClick={del} data-testid="menu-item-delete-btn">Delete item</button>
    </div>
    {it.options.length > 0 && <div className="mt-3 grid gap-2">{it.options.map((g, i) => <OptionEditor key={g.id} g={g} upd={(ng) => upd({ ...it, options: it.options.map((x, j) => (j === i ? ng : x)) })} del={() => upd({ ...it, options: it.options.filter((_, j) => j !== i) })} />)}</div>}
  </div>
);

export default function MenuEditor({ api, venue, reload }) {
  const [menu, setMenu] = useState(venue.menu || []);
  const updCat = (i, c) => setMenu(menu.map((x, j) => (j === i ? c : x)));
  const save = async () => {
    try { await api.put(`/venues/${venue.id}/menu`, { menu }); toast.success("Menu saved"); reload(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="mt-4" data-testid="menu-editor">
      <div className="sticky top-16 z-10 flex items-center justify-between bg-white py-3">
        <button className="btn-secondary !py-2" onClick={() => setMenu([...menu, { id: uid(), name: "New category", items: [] }])} data-testid="menu-add-category-btn"><Plus size={16} /> Category</button>
        <button className="btn-primary !py-2" onClick={save} data-testid="menu-save-btn">Save menu</button>
      </div>
      {menu.length === 0 && <p className="text-muted2">No menu yet. Add a category to start.</p>}
      {menu.map((c, i) => (
        <section key={c.id} className="card-lg mt-4 p-4" data-testid="menu-category-editor">
          <div className="flex items-center gap-2">
            <Input className="font-heading text-lg font-extrabold" value={c.name} onChange={(e) => updCat(i, { ...c, name: e.target.value })} data-testid="menu-category-name-input" />
            <button className="text-danger" aria-label="Delete category" onClick={() => setMenu(menu.filter((_, j) => j !== i))}><Trash2 size={18} /></button>
          </div>
          <div className="mt-3 grid gap-3">
            {c.items.map((it, k) => <ItemEditor key={it.id} it={it} upd={(ni) => updCat(i, { ...c, items: c.items.map((x, j) => (j === k ? ni : x)) })} del={() => updCat(i, { ...c, items: c.items.filter((_, j) => j !== k) })} />)}
          </div>
          <button className="link-lg mt-3 text-sm" onClick={() => updCat(i, { ...c, items: [...c.items, { id: uid(), name: "", description: "", price_cents: 0, tax_exempt: false, sold_out: false, options: [] }] })} data-testid="menu-add-item-btn">+ Add item</button>
        </section>
      ))}
    </div>
  );
}
